import { IDocumentMetadata } from "contracts/documents";
import { Browser, DeviceInfo } from "../../Device/Info/DeviceInfo";
import { ISignatureProviderSettings } from "../../Global/Base/Crypto/ISignatureProvider";
import { DIContainer } from "../../Global/Base/DIContainer";
import { InvalidArgumentException, InvalidOperationException } from "../../Global/Base/Exceptions";
import { FileUtils } from "../../Global/Base/Utils/FileUtils";
import { Globalization } from "../../Global/Dom/Globalization";
import { CryptoExtensionUnavailableException, EInsufficientVersionException, NativeAppBulkException, NativeAppException, NativeAppExitedException, NativeAppSigningException, NativeAppUnavailableException } from "./CryptoExceptions";
import { ICryptedGroup, ICryptGroup, ISignatureDataStream, ISignedGroupInfo, ISignedInfo, ISignGroup, ISignGroupStream, ISignGroupWritables, ISigningAppCommunicator, ISigningSession, ISigningStream, IStream, IWritableStream } from "./CryptoInterfaces";
import { CommandType } from "./Messages/CommandType";
import { IDecryptKeys } from "./Messages/IDecryptKeys";
import { ICreateStream, IDecryptedKeys, IProviderInfo, ISignedStreams, ISignParams, IStream as MessageIStream, IStreamSize, IVersionInfo as IMessageVersionInfo } from "./Messages/MessagesInterface";
import { ReadableStream } from "./ReadableStream";
import { WritableStream } from "./WritableStream";

export class SigningSession implements ISigningSession {
    private readonly _requiredExtensionVersion = "20.0.7333";
    private readonly _requiredNativeAppVersion = "22.2.8227";
    private readonly _edgeChromiumExtensionUpdateURL = "https://microsoftedge.microsoft.com/addons/detail/mhhmkoogppocgeaedlcikkhenhagbmnj";
    private readonly _chromeExtensionUpdateURL = "https://chrome.google.com/webstore/detail/crypto-web-extension/pjohlogcennenjhkfclfgaganagadkkm";
    private readonly _operaExtensionUpdateURL = "https://download.tescosw.cz/crypto/files/opera-extension/crypto_web_extension-op.crx";
    private readonly _firefoxExtensionUpdateURL = "https://download.tescosw.cz/crypto/files/firefox-extension/crypto_web_extension-fx.xpi";
    private readonly _safariExtensionUpdateURL = ""; // TODO: doplnit URL pro Safari
    private readonly __defaultUpdateURL = "https://download.tescosw.cz/crypto/?lang={lang}";

    /**
     * Počítadlo skupin streamů
     */
    private _groupCounter = 0;

    private readonly _communicator: ISigningAppCommunicator;
    private __platform: string;

    private get _platform(): string {
        if (this.__platform === undefined)
            this._checkStateOrThrow(); // pokud existuje session provede command VersionInfo a načte z něj platformu, na které běží CNA

        return this.__platform;
    }

    private get _chunkSize(): number {
        let chunkSize = 10485760; // 10 MB - Windows nevypadá, že by měl s touto velikostí problém

        if (this._platform == "Darwin") {
            // Na MacOS při posílání zprávy delší než 65535 B (ushort.MaxSize) dojde někde mezi CWE a CNA k rozdělení zprávy a ta pak nejde parsovat.
            // Data se převádí do base64, která je nafoukne o třetinu. Navíc jsou obalena v json struktuře, takže pro jistotu necháme nějaký prostor.
            // Toto bohužel negativně ovlivní rychlost přenosu dat mezi CWE a CNA.
            chunkSize = 48128; // 65535/(4/3)=48 kB; dáme radši 47 kB (48128 B)
            // viz také TFS42338 TFS34424 https://stackoverflow.com/a/53561591 https://stackoverflow.com/a/57220500
        }

        return chunkSize;
    }

    private get _defaultUpdateURL(): string {
        return this.__defaultUpdateURL.replace("{lang}", Globalization.actualCultureISO639_1);
    }

    constructor(@DIContainer.instance.bindType(ISigningAppCommunicator) communicator: ISigningAppCommunicator) {
        this._communicator = communicator;
    }

    /**
     * Otevře současnou session, bez otevření s ní nelze pracovat
     * @param lang Nepovinný atribut určující jazyk GUI, který je potřebný pouze v případě, že se v rámci session bude otevírat GUI, jinak by se neměl posílat. Jazyk musí být ISO 639-1 (en, cs, ...).
     */
    public async open(lang?: string): Promise<void> {
        try {
            if (document.querySelector("meta[name='MultiWebCryptoExtension']") == null) // kontrola nainstalovaného rozšíření
                throw new CryptoExtensionUnavailableException(this._getBrowserSpecificExtensionURL());
            await this._communicator.openSession(lang);
        } catch (e) {
            if (e instanceof NativeAppUnavailableException)
                e.downloadURL = this._defaultUpdateURL;
            throw e;
        }
    }

    /**
     * Vytvoří stream, pomocí něhož lze zapisovat data, která se pošlou do nativní aplikace
     * @param name název podepisovaných dat
     * @param fileExtension přípona podepisovaného souboru pro soubory, jinak undefined
     * @param uri uri identifikátor podepisovaného souboru u detached podpisu (u embeded podpisů není potřeba)
     * @param hash hash podepisované souboru u detached podpisu (u embeded podpisů není potřeba)
     * @param hashAlg kód algoritmu, kterým byl vytvořen hash souboru - musí být poskytnut spolu s hashem (u embeded podpisů není potřeba)
     */
    private async _createStream(name: string, fileExtension: string, uri?: string, hash?: string, hashAlg?: string): Promise<IWritableStream> {
        if (!name)
            throw new InvalidArgumentException("name");

        if (hash && !uri)
            throw new InvalidArgumentException("uri must be specified for detached signatures");

        if (hash && !hashAlg)
            throw new InvalidArgumentException("hashAlg must be specified when hash is present");

        const body: ICreateStream = {
            name: name,
            mimeType: fileExtension,
            uri: uri,
            hashType: hashAlg,
            hash: hash,
        };

        const response = await this._communicator.postMessage(CommandType.CreateStream, body);

        const streamInfo = response.body as ICreateStream;
        if (!streamInfo || !streamInfo.streamId)
            throw new InvalidOperationException("Didn't receive streamId for new stream");

        const newStreamId = streamInfo.streamId;

        return new WritableStream(newStreamId, this._communicator);
    }

    /**
     * Uvolní vytvořené streamy
     * @param streams streamy určené k uvolnění
     */
    private async _disposeStream(...streams: IStream[]): Promise<void> {
        for (const stream of streams) {
            if (!stream || !stream.streamId)
                throw new InvalidArgumentException("stream");

            const body: MessageIStream = {
                streamId: stream.streamId,
            };

            await this._communicator.postMessage(CommandType.DisposeStream, body);
        }
    }

    /**
     * Podepíše předaný stream podle předaných informací
     * @param data data pro podepsání
     */
    private async _sendSignStreams(signingParams: ISignParams[]): Promise<ISignedStreams> {
        const response = await this._communicator.postMessage(CommandType.SignStreams, { signGroups: signingParams });
        if (!response.body)
            throw new InvalidOperationException("SignStreams response is empty");

        return response.body as ISignedStreams;
    }

    /**
     * Vytvoří podpis dokumentů
     * @param providerSettings konfigurace podpisového provideru, které určují, jaký podpis má vytvářet
     * @param inputStreams libovolný počet streamů s daty a metadaty určenými pro podpis
     */
    public async createDocumentSignatures(providerSettings: ISignatureProviderSettings, ...inputStreams: ISignatureDataStream[]): Promise<ISignedInfo[]> {
        await this._checkStateOrThrow();

        if (inputStreams.length < 1)
            throw new InvalidArgumentException("No streams to sign");

        const singleGroup: ISignGroupWritables = { streams: [], provider: providerSettings };
        for (const is of inputStreams) {
            const stream = await this._createDocumentStream(is, providerSettings);
            const displayStream = await this._createDisplayStream(is, providerSettings);
            singleGroup.streams.push({ signingStream: stream, displayStream: displayStream });
        }

        const [signedInfo] = (await this._signAndProcessStreams([singleGroup]));
        return signedInfo.infos;
    }

    /**
     * Hromadný podpis skupin dokumentů.
     * @param signingGroups Pole skupin dokumentů.
     */
    public async signDocumentGroups(...signingGroups: ISignGroup[]): Promise<ISignedGroupInfo[]> {
        return await this._signGroups(signingGroups, (stream, settings) => this._createDocumentGroupStream(stream, settings));
    }

    /**
     * Vytvoří podpis textového řetězce
     * @param signingGroups Skupiny podpisových providerů a podepisovaných objektů, které určují jaký podpis se má vytvářet pro konkrétní skupiny objektů.
     */
    public async createTextSignatures(...signingGroups: ISignGroup[]): Promise<ISignedGroupInfo[]> {
        return await this._signGroups(signingGroups, (stream, settings) => this._createTextGroupStream(stream, settings));
    }

    /**
     * Podepíše skupiny dat.
     * @param signingGroups Skupiny dat k podpisu.
     * @param groupStreamCreator Odkaz na funkci, která vytváří skupinu streamů pro podpis posílanou do CryptoNativeApp.
     */
    private async _signGroups(signingGroups: ISignGroup[], groupStreamCreator: (signingStreams: ISigningStream, providerSettings: ISignatureProviderSettings) => Promise<ISignGroupStream>): Promise<ISignedGroupInfo[]> {
        await this._checkStateOrThrow();

        if (signingGroups.length < 1 || signingGroups.some(i => i.inputStreams.length === 0))
            throw new InvalidArgumentException("At least one of signing groups has no streams to sign.");
        //Pro každou podpisovou skupinu vytvoří informace o podpisovém provideru a vytvoří datové streamy pro podepisované a zobrazované objekty.
        const streamGroups: ISignGroupWritables[] = [];
        for (const sg of signingGroups) {
            const sgw: ISignGroupWritables = { provider: sg.provider, streams: [] };
            for (const is of sg.inputStreams) {
                sgw.streams.push(await groupStreamCreator(is, sg.provider));
            }
            streamGroups.push(sgw);
        }

        return await this._signAndProcessStreams(streamGroups);
    }

    /**
     * Vytváří skupinu streamů pro podpis dokumentů, které se posílají do CryptoNativeApp.
     * @param signingStreams Data k podpisu.
     * @param providerSettings Konfigurace podpisového providera.
     */
    private async _createDocumentGroupStream(signingStreams: ISigningStream, providerSettings: ISignatureProviderSettings): Promise<ISignGroupStream> {
        return {
            signingStream: await this._createDocumentStream(signingStreams.signingStream, providerSettings),
            displayStream: await this._createDisplayStream(signingStreams.displayStream, providerSettings),
            currentSignatureStream: signingStreams.currentSignatureStream ? await this._createDocumentStream(signingStreams.currentSignatureStream, providerSettings) : undefined,
        };
    }

    /**
    * Vytváří skupinu streamů pro podpis textu, které se posílají do CryptoNativeApp.
    * @param signingStreams Data k podpisu.
    * @param providerSettings Konfigurace podpisového providera.
    */
    private async _createTextGroupStream(signingStreams: ISigningStream, providerSettings: ISignatureProviderSettings): Promise<ISignGroupStream> {
        return {
            signingStream: await this._createSimpleStream(signingStreams.signingStream, providerSettings),
            displayStream: (signingStreams.displayStream) ? await this._createSimpleStream(signingStreams.displayStream, providerSettings) : await this._createSimpleStream(signingStreams.signingStream, providerSettings),
            currentSignatureStream: signingStreams.currentSignatureStream ? await this._createSimpleStream(signingStreams.currentSignatureStream, providerSettings) : undefined,
        };
    }

    /**
     * Vytvoří kontra podpis dokumentu
     * @param providerSettings konfigurace podpisového provideru, které určují, jaký podpis má vytvářet
     * @param signatureStream stream ve kterém je stávající podpis
     * @param detachedStream stream s daty v případě detached podpisu
     */
    public async createCounterSignature(providerSettings: ISignatureProviderSettings, signatureStream: ISignatureDataStream, detachedStream?: ISignatureDataStream): Promise<ISignedInfo[]> {
        await this._checkStateOrThrow();

        if (providerSettings.counterSignatureProfile != null) {
            providerSettings.signatureType = providerSettings.counterSignatureProfile.signatureType;
            providerSettings.otherParams = providerSettings.counterSignatureProfile.otherParams;
        }

        const singleGroup: ISignGroupWritables = { streams: [], provider: providerSettings };
        const signStream = await this._createDocumentStream(signatureStream, providerSettings);
        const dataStream = detachedStream ? await this._createDocumentStream(detachedStream, providerSettings) : signStream;
        const displayStream = detachedStream ? await this._createDisplayStream(detachedStream, providerSettings) : signStream;
        singleGroup.streams.push({ signingStream: dataStream, displayStream: displayStream, currentSignatureStream: signStream });

        const [signedInfo] = (await this._signAndProcessStreams([singleGroup]));
        return signedInfo.infos;
    }

    public async decryptObjects(data: ICryptGroup): Promise<ICryptedGroup> {
        await this._checkStateOrThrow();

        return await this._decryptKeys(data);
    }

    private async _signAndProcessStreams(streamGroups: ISignGroupWritables[]): Promise<ISignedGroupInfo[]> {
        try {
            const signedData = await this._signStreams(streamGroups);

            if (streamGroups.length !== signedData.length)
                throw new InvalidOperationException(`Number of groups for signing was ${streamGroups.length} but received ${signedData.length} signed groups`); //`

            //Projde skupiny podpisů, které se odeslaly do nativky s vrácenými a uvolní streamy, které byly přepsány.
            //Předpokládá se, že nativka vrátí informace o podepsaných skupinách ve stejném pořadí, jako ji byly předány.
            for (let g = 0; g < signedData.length; g++) {
                const strGrp = streamGroups[g];
                const sgnGrp = signedData[g];

                if (strGrp.streams.length !== sgnGrp.infos.length)
                    throw new InvalidOperationException(`Number of streams in group ${sgnGrp.groupId} for signing was ${sgnGrp.infos.length} but received ${sgnGrp.infos.length} signed streams`);

                for (let i = 0; i < sgnGrp.infos.length; ++i) {
                    const stream = strGrp.streams[i];
                    const signedInfo = sgnGrp.infos[i];

                    // starý stream se uvolní, pokud byl pro výsledek vytvořen stream nový
                    if (signedInfo.stream && signedInfo.stream.streamId !== stream.signingStream.streamId)
                        await this._disposeStream(stream.signingStream);
                }
            }
            return signedData;
        } catch (e) {
            if (!(e instanceof NativeAppExitedException)) {
                try {
                    await Promise.all(streamGroups.map(g => this._disposeStream(...g.streams.map(s => s.signingStream), ...g.streams.map(d => d.displayStream))));
                } catch (ignore) {
                    // Prázdný catch
                } // Pokud by došlo k chybě při mazání streamů, tak IE zobrazí tuto chybu a ne výsledek z nativky (řešeno pro LW)
            }

            throw e;
        }
    }

    private async _createSimpleStream(inputStream: ISignatureDataStream, _providerSettings: ISignatureProviderSettings): Promise<IWritableStream> {
        const objectName = await inputStream.getName();

        const stream = await this._createStream(objectName, ".txt", await inputStream.getReferenceUri());

        await inputStream.read(this._chunkSize, async (chunk: ArrayBuffer, isLastChunk: boolean) => {
            await stream.writeBlock(chunk, isLastChunk);
        });

        stream.name = objectName;

        return stream;
    }

    private async _createDocumentStream(inputStream: ISignatureDataStream, providerSettings: ISignatureProviderSettings): Promise<IWritableStream> {
        const fileName = await inputStream.getName();
        let stream: IWritableStream;
        let fileMetadata: IDocumentMetadata | null;

        // na základě informace o tom, jestli se bude vytvářet detached nebo embedded podpis se rozhodne,
        // jestli je potřeba stáhnout celý soubor (pro embedded podpisy) nebo stačí získat hash souboru (detached podpisy)
        if (providerSettings.signatureInsideDocument || (fileMetadata = await inputStream.getMetadata()) == null) {
            stream = await this._createStream(fileName, FileUtils.getFileExtension(fileName), await inputStream.getReferenceUri());
            await inputStream.read(this._chunkSize, async (chunk: ArrayBuffer, isLastChunk: boolean) => {
                await stream.writeBlock(chunk, isLastChunk);
            });
        } else {
            stream = await this._createStream(fileName, FileUtils.getFileExtension(fileName), await inputStream.getReferenceUri(), SigningSession._getDocumentHash(fileMetadata), fileMetadata.hashAlgorithm);
        }

        stream.name = fileName;

        return stream;
    }

    private async _createDisplayStream(inputStream: ISignatureDataStream, providerSettings: ISignatureProviderSettings): Promise<IWritableStream> {
        const fileName = await inputStream.getName();
        let stream: IWritableStream;
        let fileMetadata: IDocumentMetadata | null;

        if (SignatureType[providerSettings.signatureType as "Detached" | "Enveloping" | "Enveloped"] == SignatureType.Enveloping) {
            stream = await this._createStream(fileName, FileUtils.getFileExtension(fileName), await inputStream.getReferenceUri());
            await inputStream.read(this._chunkSize, async (chunk: ArrayBuffer, isLastChunk: boolean) => {
                await stream.writeBlock(chunk, isLastChunk);
            });
        } else if (providerSettings.signatureInsideDocument || (fileMetadata = await inputStream.getMetadata()) == null) {
            stream = await this._createStream(fileName, FileUtils.getFileExtension(fileName), await inputStream.getReferenceUri());
        } else {
            stream = await this._createStream(fileName, FileUtils.getFileExtension(fileName), await inputStream.getReferenceUri(), SigningSession._getDocumentHash(fileMetadata), fileMetadata.hashAlgorithm);
        }

        stream.name = fileName;

        return stream;
    }

    private static _getDocumentHash(metadata: IDocumentMetadata): string {
        return metadata.originalHash ? metadata.originalHash : metadata.hash;
    }

    private async _signStreams(streamGroups: ISignGroupWritables[]): Promise<ISignedGroupInfo[]> {
        const signingParams: ISignParams[] = streamGroups.map(sg => {
            const settings = sg.provider.otherParams ? (typeof sg.provider.otherParams === "object" ? sg.provider.otherParams : JSON.parse(sg.provider.otherParams)) : undefined; // settings je v nativní aplikaci implementován jako JSON, tak by to asi mělo být nějak vyřešeno i tady

            const providerInfo: IProviderInfo = {
                type: sg.provider.providerType,
                version: sg.provider.providerVersion,
                settings: sg.provider.signatureType ? { ...settings, signatureType: SignatureType[sg.provider.signatureType as any] } : settings,
                selectionParams: sg.provider.selectionParams,
            };

            return {
                groupId: (this._groupCounter++).toString(),
                provider: providerInfo,
                streams: sg.streams.map(s => {
                    return { displayedStream: s.displayStream.streamId, signingStream: s.signingStream.streamId, currentSignatureStream: s.currentSignatureStream ? s.currentSignatureStream.streamId : undefined };
                }),
            };
        });

        const signedStreams = await this._sendSignStreams(signingParams);

        if (!signedStreams || signedStreams.signedGroups == null)
            throw new InvalidOperationException("Invalid sign response - groups empty");

        const bulkException = new NativeAppBulkException();
        const signedInfo: ISignedGroupInfo[] = [];
        for (const sg of signedStreams.signedGroups) {
            const groupResult: ISignedGroupInfo = { groupId: sg.groupId, infos: [] };
            for (const ss of sg.signedStreams) {
                const [stream, provider] = SigningSession._findStream(streamGroups, ss.streamId);
                const inst = {
                    stream: ss.signedStreamId
                        ? new ReadableStream(ss.signedStreamId, this._communicator, await this._getStreamSize(ss.signedStreamId), stream!.name)
                        : undefined,
                    error: ss.error ? new NativeAppSigningException(ss.streamId, stream!.name, ss.error.message) : undefined,
                    containsDocument: provider!.signatureInsideDocument,
                };

                groupResult.infos.push(inst);
                if (inst.error)
                    bulkException.addError(inst.error);
            }
            signedInfo.push(groupResult);
        }
        if (bulkException.hasAnyError)
            throw bulkException;
        return signedInfo;
    }

    /**
     * Podle zadaného ID najde podepisovaný stream
     * @param streamGroups Skupiny podepisovaných streamů
     * @param streamId ID streamu
     */
    private static _findStream(streamGroups: ISignGroupWritables[], streamId: string): [IWritableStream, ISignatureProviderSettings] {
        for (const g of streamGroups) {
            for (const s of g.streams) {
                if (s.signingStream.streamId === streamId)
                    return [s.signingStream, g.provider];
            }
        }
        throw new NativeAppException("One or more streams were not signed.");
    }

    private async _getStreamSize(streamId: string): Promise<number> {
        try {
            return new Promise<number>(async resolve => {
                const msgBody: IStream = {
                    streamId: streamId,
                };

                const response = await this._communicator.postMessage(CommandType.GetStreamSize, msgBody);
                if (!response.body || (response.body as IStreamSize).streamSize == null)
                    throw new InvalidOperationException("ReadableStream didn't receive stream size.");

                resolve((response.body as IStreamSize).streamSize);
            });
        } catch (e) {
            throw new InvalidOperationException("Unable to get stream size.");
        }
    }

    private async _decryptKeys(msgBody: IDecryptKeys): Promise<IDecryptedKeys> {
        try {
            const response = await this._communicator.postMessage(CommandType.DecryptKeys, msgBody);
            const data = response.body as IDecryptedKeys;

            for (const item of data.keys) {
                if (item.error)
                    throw new InvalidArgumentException(`${item.error.type}:${item.error.message}`); // `
            }

            return data;
        } catch (e) {
            throw new InvalidOperationException(`Decryption error: ${e instanceof Error ? e.toString() : "unknown"}`); // `
        }
    }

    /**
     * Zkontroluje vnitřní stav (zda je otevřená session, zda jsou splněné minimální verze, ...) a pokud není v pořádku, tak vyhodí výjimku
     */
    private _checkStateOrThrow(): Promise<void> {
        this._ensureSession();
        return this._checkRequiredVersions();
    }

    /**
     * Zkontroluje, zda existuje otevřená session a pokud ne, tak vyhodí InvalidOperationException
     */
    private _ensureSession(): void {
        if (!this._communicator.sessionId)
            throw new InvalidOperationException("Session unavailable");
    }

    /**
     * Vrátí URL pro stažení rozšíření podle použitého prohlížeče
     */
    private _getBrowserSpecificExtensionURL(): string {
        let resultURL: string;

        switch (DeviceInfo.deviceProperties.browser) {
            case Browser.chrome:
                resultURL = this._chromeExtensionUpdateURL;
                break;
            case Browser.opera:
                resultURL = this._operaExtensionUpdateURL;
                break;
            case Browser.edgeChromium:
                resultURL = this._edgeChromiumExtensionUpdateURL;
                break;
            case Browser.firefox:
                resultURL = this._firefoxExtensionUpdateURL;
                break;
            case Browser.safari:
                resultURL = this._safariExtensionUpdateURL;
                break;
            default:
                resultURL = this._defaultUpdateURL;
                break;
        }

        return resultURL;
    }

    /**
     * Zjistí informace o verzi extension a nativní aplikace
     */
    private async _getVersionInfo(): Promise<IVersionInfo> {
        const response = await this._communicator.postMessage(CommandType.VersionInfo);
        const messageVersionInfo = (response.body as IMessageVersionInfo);

        const versionPair = messageVersionInfo.version.split(";");
        this.__platform = messageVersionInfo.platform;

        const [extension, nativeComponent] = versionPair;
        const versionInfo = {
            extension,
            nativeComponent,
        };

        return versionInfo;
    }

    /**
     * Zkontroluje, zda jsou nainstalované požadované verze rozšíření a nativní aplikace.
     * Pokud ne, vyhodí výjimku.
     */
    private async _checkRequiredVersions(): Promise<void> {
        const versionInfo = await this._getVersionInfo(); // dotaz na verzi extensiony a nativní aplikace

        const extensionVersionSufficient = SigningSession._compareVersions(this._requiredExtensionVersion, versionInfo.extension);
        const nativeAppVersionSufficient = SigningSession._compareVersions(this._requiredNativeAppVersion, versionInfo.nativeComponent);

        if (!extensionVersionSufficient || !nativeAppVersionSufficient) {
            throw new EInsufficientVersionException(extensionVersionSufficient,
                nativeAppVersionSufficient,
                this._requiredExtensionVersion,
                versionInfo.extension,
                this._requiredNativeAppVersion,
                versionInfo.nativeComponent,
                this._getBrowserSpecificExtensionURL(),
                this._defaultUpdateURL);
        }
    }

    /**
     * Porovná, zda je nainstalovaná požadovaná verze nebo vyšší
     * @param required Minimální vyžadovaná verze
     * @param installed Nainstalovaná verze
     */
    private static _compareVersions(required: string, installed: string): boolean {
        const requiredParts = required.split(".");
        const installedParts = installed.split(".");
        const maxPartsCount = Math.max(requiredParts.length, installedParts.length);

        for (let i = 0; i < maxPartsCount; i++) {
            const installedPart = (installedParts[i] && parseInt(installedParts[i])) || 0; // pokud část chybí a použije se nula
            const requiredPart = (requiredParts[i] && parseInt(requiredParts[i])) || 0;
            if (installedPart > requiredPart)
                return true;
            else if (installedPart < requiredPart)
                return false;
        }

        return true;
    }

    public dispose(): void {
        this._communicator.dispose();
    }
}

interface IVersionInfo {
    extension: string;
    nativeComponent: string;
}

// ReSharper disable InconsistentNaming
enum SignatureType { Detached, Enveloping, Enveloped } // TElXMLSignatureType = (xstDetached, xstEnveloping, xstEnveloped); v Nativce
// ReSharper restore InconsistentNaming

DIContainer.instance.register(ISigningSession, SigningSession, false);
